import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Heart, Upload, X, Loader2, ImageIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";

export default function DedicationModal({ open, onOpenChange, count, unitPrice, pixels }) {
  const [buyerName, setBuyerName] = useState("");
  const [dedicatedTo, setDedicatedTo] = useState("");
  const [message, setMessage] = useState("");
  const [photoPath, setPhotoPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const total = (count * unitPrice).toFixed(2);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoPath(data.path);
      toast.success("Foto caricata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nel caricamento della foto");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!buyerName.trim() || !dedicatedTo.trim()) {
      toast.error("Inserisci il tuo nome e il nome della persona amata");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkout", {
        pixels: pixels || [],
        buyer_name: buyerName.trim(),
        dedicated_to: dedicatedTo.trim(),
        message: message.trim(),
        photo_path: photoPath,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.taken) {
        toast.error(`${detail.taken.length} pixel sono appena stati presi. Riprova con altri.`);
        onOpenChange(false);
      } else {
        toast.error(typeof detail === "string" ? detail : "Impossibile avviare il pagamento");
      }
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="dedication-modal"
        className="sm:max-w-[820px] p-0 gap-0 overflow-hidden border-rose-900/40 bg-slate-950 max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-rose-900/30">
          <DialogTitle className="font-display text-2xl text-rose-50 flex items-center gap-2">
            <Heart className="text-rose-500 fill-rose-500" size={22} /> Dedica il tuo amore
          </DialogTitle>
          <p className="text-sm text-slate-400 mt-1">
            {count} pixel · <span className="text-amber-400 font-semibold">€{total}</span> — un messaggio per sempre sul muro
          </p>
        </DialogHeader>

        <div className="grid md:grid-cols-2">
          {/* form */}
          <div className="p-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="buyer" className="text-slate-300">Il tuo nome</Label>
              <Input id="buyer" data-testid="dedication-name-input" value={buyerName}
                onChange={(e) => setBuyerName(e.target.value)} maxLength={80}
                placeholder="Es. Marco" className="bg-slate-900 border-slate-700" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dedicated" className="text-slate-300">Dedicato a</Label>
              <Input id="dedicated" data-testid="dedication-to-input" value={dedicatedTo}
                onChange={(e) => setDedicatedTo(e.target.value)} maxLength={80}
                placeholder="Es. Giulia" className="bg-slate-900 border-slate-700" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="message" className="text-slate-300">Messaggio d'amore</Label>
              <Textarea id="message" data-testid="dedication-message-input" value={message}
                onChange={(e) => setMessage(e.target.value)} maxLength={280} rows={3}
                placeholder="Ti amo oggi, domani e per sempre..." className="bg-slate-900 border-slate-700 resize-none" />
              <p className="text-xs text-slate-500 text-right">{message.length}/280</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300">Foto (opzionale)</Label>
              <input ref={fileRef} type="file" accept="image/*" hidden
                onChange={(e) => handleFile(e.target.files?.[0])} data-testid="dedication-photo-input" />
              {photoPath ? (
                <div className="relative w-full h-28 rounded-lg overflow-hidden border border-slate-700">
                  <img src={fileUrl(photoPath)} alt="anteprima" className="w-full h-full object-cover" />
                  <button onClick={() => setPhotoPath(null)}
                    className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center rounded-full bg-black/60 text-white hover:bg-rose-600">
                    <X size={15} />
                  </button>
                </div>
              ) : (
                <button data-testid="upload-photo-button" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="w-full h-28 rounded-lg border-2 border-dashed border-slate-700 grid place-items-center text-slate-400 hover:border-rose-500 hover:text-rose-300 transition-colors">
                  {uploading ? <Loader2 className="animate-spin" /> : (
                    <span className="flex flex-col items-center gap-1 text-sm"><Upload size={20} /> Carica una foto</span>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* preview */}
          <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-950 border-l border-rose-900/20 flex flex-col">
            <p className="text-xs uppercase tracking-widest text-slate-500 mb-3">Anteprima dedica</p>
            <div className="flex-1 rounded-xl border border-rose-900/40 bg-[#16223f] p-5 relative overflow-hidden">
              <div className="absolute inset-0 opacity-20"
                style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #e11d48 0 2px, transparent 3px), radial-gradient(circle at 70% 60%, #e11d48 0 2px, transparent 3px)" }} />
              {photoPath && (
                <img src={fileUrl(photoPath)} alt="dedica" className="w-16 h-16 rounded-full object-cover border-2 border-rose-400 mb-3 relative" />
              )}
              <Heart className="text-rose-500 fill-rose-500 mb-2 relative animate-heart-pulse" size={26} />
              <p className="font-hand text-3xl text-rose-100 leading-tight relative">
                {dedicatedTo ? `Per ${dedicatedTo}` : "Per la persona amata"}
              </p>
              <p className="font-hand text-xl text-rose-200/90 mt-2 relative min-h-[28px]">
                {message || "Il tuo messaggio apparirà qui"}
              </p>
              <p className="text-sm text-amber-300/90 mt-4 relative">
                — {buyerName || "il tuo nome"}
              </p>
            </div>

            <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
              <ImageIcon size={14} /> {count} pixel sul muro di Parigi
            </div>
            <Button
              data-testid="paypal-pay-button"
              onClick={submit}
              disabled={submitting}
              className="mt-3 w-full h-12 text-base bg-rose-600 hover:bg-rose-500 text-white rounded-full font-semibold"
            >
              {submitting ? <Loader2 className="animate-spin" /> : <>Paga €{total} in sicurezza</>}
            </Button>
            <p className="mt-2 flex items-center justify-center gap-1.5 text-[11px] text-slate-500">
              <ShieldCheck size={13} /> Pagamento sicuro con carta · Stripe
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
